import { RENDER_PALETTE } from "./render-palette";

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  color: number;
  alpha: number;
  size: number;
}

interface EffectBurst {
  particles: Particle[];
  centerX: number;
  centerY: number;
  ghostSize: number;
  ghostColor: number;
  ghostAlpha: number;
  ringSize: number;
  ringAlpha: number;
  elapsedMs: number;
  durationMs: number;
  dead: boolean;
}

const PARTICLE_COUNT_MIN = 40;
const PARTICLE_COUNT_MAX = 52;
const PARTICLE_SPEED_MIN = 60;
const PARTICLE_SPEED_MAX = 160;
const PARTICLE_SIZE_MIN = 0.5;
const PARTICLE_SIZE_MAX = 1.5;
const PARTICLE_FRICTION = 0.95;
const RING_EXPAND_PX = 72;
const RING_LINE_WIDTH = 2;

function randomRange(min: number, max: number): number {
  return min + Math.random() * (max - min);
}

function createParticle(centerX: number, centerY: number): Particle {
  const angle = Math.random() * Math.PI * 2;
  const speed = randomRange(PARTICLE_SPEED_MIN, PARTICLE_SPEED_MAX);
  const colors = RENDER_PALETTE.effects.burstColors;
  const color = colors[Math.floor(Math.random() * colors.length)] ?? colors[0]!;

  return {
    x: centerX,
    y: centerY,
    vx: Math.cos(angle) * speed,
    vy: Math.sin(angle) * speed,
    color,
    alpha: 1.0,
    size: randomRange(PARTICLE_SIZE_MIN, PARTICLE_SIZE_MAX),
  };
}

function createBurst(
  centerX: number,
  centerY: number,
  collectibleSize: number,
  collectibleColor: number,
): EffectBurst {
  const particleCount = Math.floor(randomRange(PARTICLE_COUNT_MIN, PARTICLE_COUNT_MAX + 1));
  const particles: Particle[] = [];

  for (let i = 0; i < particleCount; i += 1) {
    particles.push(createParticle(centerX, centerY));
  }

  return {
    particles,
    centerX,
    centerY,
    ghostSize: collectibleSize,
    ghostColor: collectibleColor,
    ghostAlpha: 1.0,
    ringSize: collectibleSize,
    ringAlpha: 1.0,
    elapsedMs: 0,
    durationMs: 500,
    dead: false,
  };
}

export class CollectibleEffectRenderer {
  private readonly graphics: Phaser.GameObjects.Graphics;
  private readonly bursts: EffectBurst[] = [];

  constructor(scene: Phaser.Scene) {
    this.graphics = scene.add.graphics();
    this.graphics.setDepth(2);
  }

  public spawn(
    pixelX: number,
    pixelY: number,
    collectibleSize: number,
    collectibleColor: number,
  ): void {
    const centerX = pixelX + collectibleSize / 2;
    const centerY = pixelY + collectibleSize / 2;
    this.bursts.push(createBurst(centerX, centerY, collectibleSize, collectibleColor));
  }

  public update(delta: number): void {
    const deltaSec = delta / 1000;

    for (let i = 0; i < this.bursts.length; i += 1) {
      const burst = this.bursts[i];
      if (burst === undefined) continue;

      burst.elapsedMs += delta;

      const progress = Math.min(burst.elapsedMs / burst.durationMs, 1);

      for (let j = 0; j < burst.particles.length; j += 1) {
        const p = burst.particles[j];
        if (p === undefined) continue;

        p.x += p.vx * deltaSec;
        p.y += p.vy * deltaSec;
        p.vx *= PARTICLE_FRICTION;
        p.vy *= PARTICLE_FRICTION;
        p.alpha = 1 - progress;
      }

      burst.ghostAlpha = 1 - progress;
      burst.ringSize = burst.ghostSize + progress * RING_EXPAND_PX;
      burst.ringAlpha = 1 - progress;

      if (burst.elapsedMs >= burst.durationMs) {
        burst.dead = true;
      }
    }

    for (let i = this.bursts.length - 1; i >= 0; i -= 1) {
      if (this.bursts[i]?.dead === true) {
        this.bursts.splice(i, 1);
      }
    }
  }

  public render(): void {
    this.graphics.clear();

    for (const burst of this.bursts) {
      const halfRing = burst.ringSize / 2;
      this.graphics.lineStyle(RING_LINE_WIDTH, burst.ghostColor, burst.ringAlpha);
      this.graphics.strokeRect(
        burst.centerX - halfRing,
        burst.centerY - halfRing,
        burst.ringSize,
        burst.ringSize,
      );

      const halfGhost = burst.ghostSize / 2;
      this.graphics.fillStyle(burst.ghostColor, burst.ghostAlpha);
      this.graphics.fillRect(
        burst.centerX - halfGhost,
        burst.centerY - halfGhost,
        burst.ghostSize,
        burst.ghostSize,
      );

      for (const p of burst.particles) {
        this.graphics.fillStyle(p.color, p.alpha);
        this.graphics.fillCircle(p.x, p.y, p.size);
      }
    }
  }

  public destroy(): void {
    this.graphics.destroy();
  }
}
